/**
 * FP3-088 — Permanent Delete modal (Org Admin Students page).
 *
 * NOT Archive, NOT Soft Delete: physically removes the student and
 * permitted setup records. Runs the server preview on open, shows
 * eligible vs blocked students (with blocking-record counts), and
 * requires: a reason, the typed phrase PERMANENTLY DELETE, and (for
 * bulk) the exact eligible count. Blocked students are never deleted.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";

import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const PHRASE = "PERMANENTLY DELETE";

const REASONS = [
  { key: "withdrew_before_services", label: "Withdrew before services began" },
  { key: "never_attended", label: "Never attended" },
  { key: "enrollment_cancelled", label: "Enrollment was cancelled" },
  { key: "duplicate_student", label: "Duplicate student" },
  { key: "accidental_import", label: "Accidental import" },
  { key: "created_by_mistake", label: "Created by mistake" },
  { key: "other", label: "Other" },
];

const DEP_LABELS = {
  student_provider_assignments: "provider assignments",
  student_constraints: "constraints",
  student_coverage_assignments: "coverage assignments",
  student_snapshots: "roster snapshot rows",
  recurring_blocks: "schedule blocks",
  scheduled_session_shells: "unstarted session shells",
  notification_logs: "notification log references",
  recurring_block_archives: "archived block references",
};

const BLOCK_LABELS = {
  sessions_with_history: "delivered / started sessions",
  goals: "goals",
  goal_evidence: "goal evidence",
  student_goal_plans: "goal plans",
  student_goal_plan_targets: "goal plan targets",
  notes: "notes",
  student_collaboration_notes: "collaboration notes",
  student_documents: "documents",
  assessments: "assessments",
  student_assessment_runs: "assessment runs",
  trial_runs: "data collection runs",
  articulation_screener_sessions: "screener sessions",
  messages: "messages",
};

const fmtCounts = (obj, labels) =>
  Object.entries(obj || {})
    .map(([k, v]) => `${labels[k] || k}: ${v}`)
    .join(" · ");

// FP3-088 second-pass fix — whitespace-normalize the typed phrase so
// an invisible double/trailing space never blocks a legitimate
// confirmation (case and words stay strict).
const normalizeConfirm = (v) => (v || "").trim().replace(/\s+/g, " ");

export default function PermanentDeleteModal({
  open,
  onOpenChange,
  studentIds,
  contextParams,
  onDeleted,
}) {
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [phrase, setPhrase] = useState("");
  const [countInput, setCountInput] = useState("");
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState("");

  useEffect(() => {
    if (!open) {
      setPreview(null);
      setPreviewError("");
      setReason("");
      setNote("");
      setPhrase("");
      setCountInput("");
      setApplyError("");
      return;
    }
    setLoadingPreview(true);
    api
      .adminPermanentDeletePreview({ student_ids: studentIds }, contextParams)
      .then((r) => setPreview(r.data))
      .catch((e) =>
        setPreviewError(
          typeof e?.response?.data?.detail === "string"
            ? e.response.data.detail
            : e.message || "Preview failed"
        )
      )
      .finally(() => setLoadingPreview(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isBulk = (preview?.requested_count ?? studentIds.length) > 1;
  const eligibleCount = Number(preview?.eligible_count ?? 0);

  // FP3-088 second-pass fix — ONE deterministic blocker list. Apply is
  // disabled iff this list is non-empty, and the list is RENDERED so a
  // disabled button always explains itself. Blocked students in the
  // selection NEVER disable Apply.
  const phraseOk = normalizeConfirm(phrase) === PHRASE;
  const countOk = !isBulk || Number.parseInt(countInput, 10) === eligibleCount;

  const applyBlockers = [];
  if (loadingPreview || !preview) {
    applyBlockers.push("Preview has not finished.");
  } else if (eligibleCount < 1) {
    applyBlockers.push("No students are eligible.");
  } else {
    if (!reason) applyBlockers.push("Select a reason.");
    if (reason === "other" && !note.trim()) {
      applyBlockers.push("Enter an administrative note for “Other.”");
    }
    if (!phraseOk) applyBlockers.push(`Type ${PHRASE} to confirm.`);
    if (!countOk) {
      applyBlockers.push(`Type ${eligibleCount} to confirm the eligible count.`);
    }
  }
  if (applying) applyBlockers.push("Deletion is already processing.");
  const canApply = applyBlockers.length === 0;

  const apply = async () => {
    setApplying(true);
    setApplyError("");
    try {
      const r = await api.adminPermanentDeleteApply(
        {
          student_ids: studentIds,
          reason,
          note,
          expected_eligible_count: eligibleCount,
          confirm: normalizeConfirm(phrase),
        },
        contextParams
      );
      const d = r.data;
      const nDel = d.deleted_count || 0;
      const nBlocked = d.blocked_count || 0;
      let msg = `${nDel} ${nDel === 1 ? "student" : "students"} permanently deleted.`;
      if (nBlocked > 0) {
        msg += ` ${nBlocked} ${nBlocked === 1 ? "student was" : "students were"} not deleted because service history exists.`;
      }
      if ((d.failed_count || 0) > 0) {
        toast.error(
          `${d.failed_count} deletion(s) did not fully complete — see the audit log. ${msg}`
        );
      } else {
        toast.success(msg);
      }
      onDeleted?.((d.deleted || []).map((x) => x.student_id));
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409) {
        setApplyError(
          (typeof detail === "object" && detail?.message) ||
            "The eligible set changed since preview. Close and reopen to re-run the preview."
        );
      } else {
        setApplyError(
          (typeof detail === "string" && detail) ||
            (typeof detail === "object" && detail?.message) ||
            e.message ||
            "Permanent deletion failed"
        );
      }
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl max-h-[85vh] overflow-y-auto"
        data-testid="permanent-delete-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-[#7A1E1E]">
            {isBulk ? "Permanently delete selected students" : "Permanently delete student"}
          </DialogTitle>
          <DialogDescription data-testid="permanent-delete-warning">
            This permanently removes the student and cannot be undone. It is
            intended for students who withdrew or left before services began.
          </DialogDescription>
        </DialogHeader>

        {loadingPreview && (
          <p className="text-[12px] text-[#6B7280]" data-testid="permanent-delete-loading">
            Checking service history…
          </p>
        )}
        {previewError && (
          <p className="text-[12px] text-[#B85C5C]" data-testid="permanent-delete-preview-error">
            {previewError}
          </p>
        )}

        {preview && (
          <div className="space-y-3">
            <p className="text-[12px] text-[#1F2937]" data-testid="permanent-delete-counts">
              <span className="font-semibold tabular-nums">{preview.requested_count}</span> selected ·{" "}
              <span className="font-semibold text-[#1E5A3F] tabular-nums">{preview.eligible_count}</span> eligible ·{" "}
              <span className="font-semibold text-[#B85C5C] tabular-nums">{preview.blocked_count}</span> blocked
            </p>

            {preview.eligible.length > 0 && (
              <div className="rounded-md border border-[#D8E5DC] bg-[#F4F8F5] p-2.5 space-y-1.5" data-testid="permanent-delete-eligible-list">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-[#1E5A3F]">
                  Will be permanently deleted
                </p>
                {preview.eligible.map((s) => (
                  <div key={s.student_id} data-testid={`permadelete-eligible-${s.student_id}`}>
                    <p className="text-[12px] font-medium text-[#1F2937]">{s.name}</p>
                    <p className="text-[10.5px] text-[#6B7280]">
                      {Object.keys(s.dependent_records_to_delete || {}).length > 0
                        ? `Removes: ${fmtCounts(s.dependent_records_to_delete, DEP_LABELS)}`
                        : "No dependent setup records"}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {preview.blocked.length > 0 && (
              <div className="rounded-md border border-[#EAD5D0] bg-[#FBF3F1] p-2.5 space-y-1.5" data-testid="permanent-delete-blocked-list">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-[#B85C5C]">
                  Blocked — will NOT be deleted
                </p>
                {preview.blocked.map((s) => (
                  <div key={s.student_id} data-testid={`permadelete-blocked-${s.student_id}`}>
                    <p className="text-[12px] font-medium text-[#1F2937]">{s.name}</p>
                    <p className="text-[10.5px] text-[#B85C5C]">
                      {fmtCounts(s.blocking_records, BLOCK_LABELS)}
                    </p>
                    <p className="text-[10.5px] text-[#6B7280]">
                      This student has service or evaluation history and cannot be
                      permanently deleted. Use Archive / Exit instead.
                    </p>
                  </div>
                ))}
              </div>
            )}

            {eligibleCount > 0 && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-[11px]">Reason (required)</Label>
                  <Select value={reason} onValueChange={setReason}>
                    <SelectTrigger data-testid="permanent-delete-reason" className="h-8 text-[12px]">
                      <SelectValue placeholder="Select a reason…" />
                    </SelectTrigger>
                    <SelectContent>
                      {REASONS.map((r) => (
                        <SelectItem key={r.key} value={r.key} data-testid={`permadelete-reason-${r.key}`}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-[11px]">
                    Administrative note{" "}
                    {reason === "other" ? "(required for “Other”)" : "(optional)"}
                  </Label>
                  <Textarea
                    data-testid="permanent-delete-note"
                    value={note}
                    onChange={(e) => setNote(e.target.value.slice(0, 500))}
                    rows={2}
                    className="text-[12px]"
                    placeholder="Short operational note (max 500 characters)"
                  />
                  <p className="text-[10px] text-[#9C6B2E]" data-testid="permanent-delete-note-warning">
                    Do not include student names, medical information, evaluation
                    details, or service content.
                  </p>
                </div>

                {isBulk && (
                  <div className="space-y-1.5">
                    <Label className="text-[11px]">
                      You are permanently deleting{" "}
                      <span className="font-semibold tabular-nums">{eligibleCount}</span>{" "}
                      {eligibleCount === 1 ? "student" : "students"}. Type the number to confirm.
                    </Label>
                    <Input
                      data-testid="permanent-delete-count-input"
                      value={countInput}
                      onChange={(e) => setCountInput(e.target.value)}
                      className="h-8 w-24 text-[12px] tabular-nums"
                      placeholder={String(eligibleCount)}
                    />
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label className="text-[11px]">
                    Type <span className="font-mono font-semibold">{PHRASE}</span> to confirm
                  </Label>
                  <Input
                    data-testid="permanent-delete-phrase-input"
                    value={phrase}
                    onChange={(e) => setPhrase(e.target.value)}
                    className="h-8 text-[12px] font-mono"
                    placeholder={PHRASE}
                  />
                </div>
              </>
            )}

            {/* FP3-088 — every reason the Apply button is currently
                disabled, rendered live. Never a mystery. */}
            {!applying && applyBlockers.length > 0 && (
              <ul
                className="space-y-0.5 rounded-md border border-[#E0CFA8]/60 bg-[#FDF8F3] px-2.5 py-2"
                data-testid="permanent-delete-blockers"
                aria-live="polite"
              >
                {applyBlockers.map((b) => (
                  <li key={b} className="text-[10.5px] text-[#9C6B2E]" data-testid="permanent-delete-blocker-item">
                    • {b}
                  </li>
                ))}
              </ul>
            )}

            {applyError && (
              <p className="text-[12px] text-[#B85C5C]" data-testid="permanent-delete-apply-error">
                {applyError}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            data-testid="permanent-delete-cancel-btn"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            data-testid="permanent-delete-apply-btn"
            size="sm"
            disabled={!canApply}
            onClick={apply}
            className="bg-[#7A1E1E] hover:bg-[#611414] text-white disabled:opacity-40"
          >
            {applying
              ? "Deleting…"
              : `Permanently delete${eligibleCount > 0 ? ` ${eligibleCount}` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
